import React, { useEffect, useState } from "react";
import axiosInstance from "src/api/axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const Chats: React.FC = () => {
  const [users, setUsers] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [igId, setIgId] = useState<string | null>(null);

  // Fetch IG ID from /instagram-business/user
  useEffect(() => {
    const fetchIgId = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/user`,
          accessToken ? { headers: { Authorization: `Bearer ${accessToken}` } } : undefined
        );
        const userData = response.data && response.data.data && response.data.data[0];
        if (userData && userData.user_id) {
          setIgId(userData.user_id);
        }
      } catch (err: any) {
        setError("Failed to fetch IG ID");
      }
    };
    fetchIgId();
  }, []);

  // Fetch chat users
  useEffect(() => {
    if (!igId) return;
    const fetchUsers = async () => {
      setLoadingUsers(true);
      setError(null);
      try {
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/chats?ig_id=${igId}`
        );
        setUsers(response.data.users || []);
      } catch (err: any) {
        setError("Failed to fetch chat users");
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, [igId]);

  // Fetch chat history
  useEffect(() => {
    if (!igId || !selectedUser) return;
    const fetchMessages = async () => {
      setLoadingMessages(true);
      setError(null);
      try {
        const response = await axiosInstance.get(
          `${backendUrl}/instagram-business/chat-history?ig_id=${igId}&user_id=${selectedUser}`
        );
        setMessages(response.data.messages || []);
      } catch (err: any) {
        setError("Failed to fetch chat history");
      } finally {
        setLoadingMessages(false);
      }
    };
    fetchMessages();
  }, [igId, selectedUser]);

  return (
    <div className="flex h-96 border rounded-lg overflow-hidden bg-white">
      {/* Sidebar: Users */}
      <div className="w-1/3 border-r bg-gray-50 overflow-y-auto">
        <div className="font-bold p-2 border-b">Chats</div>
        {loadingUsers ? (
          <div className="p-2 text-gray-500">Loading users...</div>
        ) : error ? (
          <div className="p-2 text-red-500">{error}</div>
        ) : users.length === 0 ? (
          <div className="p-2 text-gray-500">No chats found.</div>
        ) : (
          <ul>
            {users.map((user) => (
              <li
                key={user}
                className={`p-2 cursor-pointer hover:bg-blue-100 ${selectedUser === user ? "bg-blue-100 font-semibold" : ""}`}
                onClick={() => setSelectedUser(user)}
              >
                {user}
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* Main: Messages */}
      <div className="flex-1 flex flex-col">
        <div className="font-bold p-2 border-b">Messages</div>
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {loadingMessages ? (
            <div className="text-gray-500">Loading messages...</div>
          ) : error ? (
            <div className="text-red-500">{error}</div>
          ) : messages.length === 0 ? (
            <div className="text-gray-500">No messages found.</div>
          ) : (
            messages.map((msg, idx) => (
              <div key={msg._id || idx} className={`rounded px-3 py-2 max-w-xs ${msg.direction === "sent" ? "bg-blue-100 ml-auto text-right" : "bg-gray-100 mr-auto text-left"}`}>
                <div className="text-sm">{msg.text}</div>
                <div className="text-xs text-gray-400 mt-1">{msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ""}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Chats; 